import { useCallback, useState } from "react";
import { useAuth } from "react-oidc-context";

import {
  ApiError,
  createApiClient,
  Forbidden,
  GuardRefused,
  NotFound,
  type RequestOptions,
  Unauthenticated,
  Unavailable,
  Unprocessable,
} from "../api/client";
import type { AppConfig } from "../config";

/**
 * The states a write can be in, kept apart because each needs different UI.
 *
 * `guardRefused` in particular is not an error to swallow — the 409 body names
 * what the refusal would break, and showing a generic "failed" throws away the
 * entire point of the guard.
 */
export type WriteState =
  | { kind: "idle" }
  | { kind: "submitting" }
  | { kind: "done"; response: unknown }
  | { kind: "guardRefused"; error: GuardRefused }
  | { kind: "invalid"; error: Unprocessable }
  | { kind: "denied"; error: Forbidden }
  | { kind: "notfound"; error: NotFound }
  | { kind: "unavailable"; error: Unavailable }
  | { kind: "signedout" }
  | { kind: "error"; error: Error };

export interface UseWrite<T> {
  state: WriteState;
  submit: (path: string, init: RequestOptions) => Promise<T | undefined>;
  reset: () => void;
}

export function useWrite<T = unknown>(config: AppConfig): UseWrite<T> {
  const auth = useAuth();
  const [state, setState] = useState<WriteState>({ kind: "idle" });
  const token = auth.user?.id_token;

  const submit = useCallback(
    async (path: string, init: RequestOptions): Promise<T | undefined> => {
      setState({ kind: "submitting" });
      const api = createApiClient({ apiUrl: config.apiUrl, getIdToken: () => token });
      try {
        const response = await api.request<T>(path, init);
        setState({ kind: "done", response });
        return response;
      } catch (err: unknown) {
        if (err instanceof GuardRefused) setState({ kind: "guardRefused", error: err });
        else if (err instanceof Unprocessable) setState({ kind: "invalid", error: err });
        else if (err instanceof Forbidden) setState({ kind: "denied", error: err });
        else if (err instanceof NotFound) setState({ kind: "notfound", error: err });
        else if (err instanceof Unavailable) setState({ kind: "unavailable", error: err });
        else if (err instanceof Unauthenticated) setState({ kind: "signedout" });
        else if (err instanceof ApiError) setState({ kind: "error", error: err });
        else if (err instanceof Error) setState({ kind: "error", error: err });
        else setState({ kind: "error", error: new Error(String(err)) });
        return undefined;
      }
    },
    [config.apiUrl, token],
  );

  return { state, submit, reset: useCallback(() => setState({ kind: "idle" }), []) };
}
